import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Cpu, Code2, Server, Sparkles, CheckCircle2 } from "lucide-react";

const INQUIRY_TYPES = [
  { key: "hardware", label: "Hardware Solutions", description: "Desktops, laptops, servers, networking, CCTV, POS, peripherals.", icon: Cpu },
  { key: "software", label: "Software Development", description: "Custom web, mobile, enterprise systems, integrations.", icon: Code2 },
  { key: "infrastructure", label: "Infrastructure Solutions", description: "Cabling, network, servers, security, managed IT.", icon: Server },
  { key: "custom", label: "Custom System / Ready-Made", description: "MSR ready-made platforms or custom-fit solutions.", icon: Sparkles },
];

export default function RequestQuote() {
  const [type, setType] = useState("hardware");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", position: "", industry: "",
    budget_range: "", timeline: "", requirements: "", notes: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { inquiry_type: type, ...form });
      setDone(true);
      toast.success("RFQ submitted successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="rfq-page">
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-3">Request for Quote</div>
            <h1 className="font-heading font-extrabold text-4xl lg:text-6xl tracking-tight" data-testid="rfq-title">
              Tell us what you need.<br />We'll prepare a tailored proposal.
            </h1>
          </div>
          <p className="lg:col-span-4 text-msr-slate leading-relaxed">
            Pricing varies based on scope, deployment, integrations, infrastructure, customization, and support. Our standard turnaround is 3 working days.
          </p>
        </div>
      </section>

      <section className="bg-msr-surface border-b border-msr-line">
        <div className="msr-container py-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-3">
            <div className="overline mb-2">Choose inquiry type</div>
            {INQUIRY_TYPES.map(({ key, label, description, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setType(key)}
                className={`w-full text-left p-5 border transition-colors ${type === key ? "border-msr-blue bg-white border-l-4" : "border-msr-line bg-white hover:border-msr-blue/50"}`}
                data-testid={`rfq-type-${key}`}
              >
                <Icon size={18} className={`mb-3 ${type === key ? "text-msr-blue" : "text-msr-slate"}`} />
                <div className="font-heading font-bold">{label}</div>
                <div className="text-xs text-msr-slate mt-1">{description}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <div className="msr-card text-center py-16" data-testid="rfq-success">
                <div className="h-14 w-14 grid place-items-center bg-msr-green text-white mx-auto mb-4">
                  <CheckCircle2 size={26} />
                </div>
                <h2 className="font-heading font-extrabold text-2xl mb-3">RFQ submitted</h2>
                <p className="text-msr-slate max-w-md mx-auto">Our team is reviewing your requirements and will be in touch within 3 working days.</p>
                <button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", company: "", position: "", industry: "", budget_range: "", timeline: "", requirements: "", notes: "" }); }} className="mt-8 btn-msr-outline" data-testid="rfq-submit-another-btn">
                  Submit another RFQ
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="msr-card grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" value={form.name} onChange={(v) => update("name", v)} required testId="rfq-name-input" />
                <Field label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} required testId="rfq-email-input" />
                <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testId="rfq-phone-input" />
                <Field label="Company" value={form.company} onChange={(v) => update("company", v)} testId="rfq-company-input" />
                <Field label="Position / Title" value={form.position} onChange={(v) => update("position", v)} testId="rfq-position-input" />
                <Field label="Industry / Business Type" value={form.industry} onChange={(v) => update("industry", v)} testId="rfq-industry-input" />
                <Select
                  label="Budget Range"
                  value={form.budget_range}
                  onChange={(v) => update("budget_range", v)}
                  options={["", "Below ₱50,000", "₱50,000 – ₱100,000", "₱100,000 – ₱500,000", "₱500,000 – ₱1M", "Above ₱1M"]}
                  testId="rfq-budget-select"
                />
                <Select
                  label="Timeline"
                  value={form.timeline}
                  onChange={(v) => update("timeline", v)}
                  options={["", "Immediate", "Within 30 Days", "Within 3 Months", "Future Planning"]}
                  testId="rfq-timeline-select"
                />
                <div className="sm:col-span-2">
                  <TextArea label="Requirements *" value={form.requirements} onChange={(v) => update("requirements", v)} required rows={4} testId="rfq-requirements-input" />
                </div>
                <div className="sm:col-span-2">
                  <TextArea label="Additional Notes" value={form.notes} onChange={(v) => update("notes", v)} rows={2} testId="rfq-notes-input" />
                </div>
                <button type="submit" disabled={submitting} className="sm:col-span-2 btn-msr-primary disabled:opacity-60" data-testid="rfq-page-submit-btn">
                  {submitting ? "Submitting..." : "Submit RFQ"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line bg-white px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue" data-testid={testId} />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line bg-white px-3 py-2.5 text-sm text-msr-ink focus:outline-none focus:border-msr-blue" data-testid={testId}>
        {options.map((o) => (<option key={o} value={o}>{o || "Select..."}</option>))}
      </select>
    </label>
  );
}

function TextArea({ label, value, onChange, required, rows = 3, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <textarea rows={rows} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line bg-white px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue resize-none" data-testid={testId} />
    </label>
  );
}
